import { useState } from 'react';
import { S } from '../../domain/state.js';
import { fMoney, fShort, wD, wDY, fISO, todayISO, DAYS, MONTHS, abs$ } from '../../lib/format.js';
import { getKey, periodShort, curKey } from '../../domain/periods.js';
import { balToday, balEst, balTodayParts } from '../../domain/balance.js';
import { strictMode, project, projParts, rangeDays, rangeEnd, rangePhrase, FLOW_RANGES, lowPoint } from '../../domain/forecast.js';
import { cardChargeAmount, cardFutureEstimate, cardPace, cardChargeEvents, cardManualFor, cardCurKey } from '../../domain/card.js';
import { recNow, recStatusTxt, recFreqLabel, recCardCur, findDupRec, findUndeclaredSubs } from '../../domain/recurring.js';
import { scanCoverage } from '../../domain/coverage.js';
import { Xn, Seg, openWhy, confirmDialog, promptDialog } from '../../components/ui.jsx';
import { navigate } from '../../app/nav.js';
import { UI, setUi, useApp, ls, toast } from '../../store/ui.js';
import { saveSetting, saveBalance, setCardManual, recWrite, deleteRec, applySim, deleteCommit } from '../../store/actions.js';
import { openRecModal } from '../settings/RecModal.jsx';
import { realisticToEnd } from '../../domain/afford.js';

const ctxOf = () => ({ stress: UI.flowStress, sim: UI.flowSim });

export function FlowPage() {
  useApp();
  const per = UI.flowPer, days = rangeDays(per);
  const setPer = (v) => { ls.set('flow_per', String(v)); setUi({ flowPer: parseInt(v, 10) }); };
  return (
    <div className="stack">
      {S.set.balDate ? <FlowHero per={per} days={days} /> : <NoBalance />}
      <div className="cols interleave">
        <div className="col">
          {S.set.balDate && (
            <div className="panel" style={{ order: 3 }}>
              <div className="card-h" style={{ flexWrap: 'wrap' }}>
                <div className="sec" style={{ margin: 0 }}>תנועת החשבון</div>
                <div className="row" style={{ flexWrap: 'wrap' }}>
                  <select className="sel" value={per} onChange={(e) => setPer(e.target.value)} aria-label="טווח">
                    {FLOW_RANGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  {UI.flowStress && <button className="ubtn ubtn-sm ubtn-danger" onClick={() => setUi({ flowStress: false })}>✓ תרחיש פסימי ✕</button>}
                </div>
              </div>
              <ModePick />
              <Timeline days={days} />
            </div>
          )}
        </div>
        <div className="col sticky">
          {S.set.balDate && <div style={{ order: 1 }}><FlowStats per={per} days={days} /></div>}
          <div className="panel" style={{ order: 2 }}><div className="sec">מה מרכיב את התחזית</div><Parts per={per} days={days} /></div>
          <div style={{ order: 4 }}><DupRec /></div>
          <div style={{ order: 5 }}><UndeclaredSubs /></div>
          <div style={{ order: 6 }}><FlowSettings /></div>
        </div>
      </div>
    </div>
  );
}

/** רגיל או מחמיר. נשמר בהגדרות, ומשפיע גם על העודף החודשי ועל הכרית */
function ModePick() {
  const strict = strictMode();
  return (
    <div className="mb12">
      <Seg value={strict ? 'strict' : ''} onChange={(v) => saveSetting('fc_mode', v)} size="sm"
        options={[['', 'רגיל'], ['strict', 'מחמיר']]} />
      <div className="note mt6">
        {strict ? 'מחמיר: בתקופות הבאות נכנסת גם הערכה להוצאות שוטפות מהעו"ש (ביט, מזומן, חד-פעמיות), לפי 3 התקופות הקודמות. גם העודף החודשי מחושב איתן.'
          : 'רגיל: בתקופות הבאות רק הכנסות וחיובים קבועים וחיוב כרטיס טיפוסי. הוצאות שוטפות מהעו"ש (ביט, מזומן) לא נכללות, ולכן התחזית אופטימית יותר.'}
      </div>
    </div>
  );
}

function NoBalance() {
  return (
    <div className="period" style={{ background: 'var(--gold-l)' }}>
      <div style={{ fontSize: '1rem', fontWeight: 900, marginBottom: 6 }}>חסרה נקודת פתיחה</div>
      <div className="sub">כדי לחשב את נקודת השפל צריך יתרה עדכנית בבנק ותאריך שאליו היא נכונה. הזינו אותם בהגדרות, בשדה יתרת הפתיחה, ותחזרו לכאן.</div>
      <button className="btn btn-outline btn-sm mt12" onClick={() => navigate('Set')}>מעבר להגדרות</button>
    </div>
  );
}

function FlowHero({ per, days }) {
  const p = project(days, ctxOf()), low = lowPoint(p), R = realisticToEnd();
  const lim = Number(S.set.odLimit) || 0, neg = low.balance < 0;
  let note;
  if (lim > 0 && low.balance < -lim) note = <><b>התחזית חורגת מהמסגרת ב<Xn k="flow_limit">{fMoney(Math.abs(-lim - low.balance))}</Xn>.</b> צריך לפעול לפני התאריך הזה: לדחות חיוב, לפרוס תשלום או להזרים כסף.</>;
  else if (neg) note = <>{lim > 0 && <>ניצול של <Xn k="flow_limit">{Math.round((-low.balance / lim) * 100)}%</Xn> מהמסגרת בשיא. </>}זו הנקודה שבה נוצרת ריבית המינוס, לא סוף החודש.</>;
  else note = 'החשבון לא נכנס למינוס בתקופה הזו. אם זה נשמר חודשיים ברצף, זה הזמן להקטין את המסגרת.';
  // מבט קצר לא אמור להסתיר שפל עמוק יותר מיד אחריו
  let deeper = null;
  if (per === 1) {
    const d2 = rangeDays(2), low2 = lowPoint(project(d2, ctxOf()));
    if (low2.balance < low.balance - 100) deeper = (
      <div className="mt6 red" style={{ fontWeight: 700 }}>⚠️ בתקופה הבאה, ב-{low2.date.getDate()} ב{MONTHS[low2.date.getMonth()]}, השפל עמוק יותר: <Xn k="flow_low" arg={d2}>{fMoney(low2.balance)}</Xn>.{' '}
        <button className="link" onClick={() => { ls.set('flow_per', '2'); setUi({ flowPer: 2 }); }}>הצג עד סוף התקופה הבאה</button></div>
    );
  }
  return (
    <div className={'period ' + (neg ? 'alert-red' : 'alert-green')} style={{ padding: 22 }}>
      <SimBox days={days} lowBal={low.balance} />
      <div style={{ fontSize: '.82rem', color: 'var(--ink2)', fontWeight: 600 }}>נקודת השפל {rangePhrase(per)}{UI.flowStress ? ' · תרחיש פסימי' : ''}</div>
      <div className="big-num" style={{ fontSize: '2.6rem', margin: '6px 0 2px', color: neg ? 'var(--red)' : 'var(--green)' }}><Xn k="flow_low" arg={days}>{fMoney(low.balance)}</Xn></div>
      <div style={{ fontSize: '.9rem', color: 'var(--ink2)', fontWeight: 600 }}>יום {DAYS[low.date.getDay()]}, {low.date.getDate()} ב{MONTHS[low.date.getMonth()]}</div>
      {per === 1 && !UI.flowStress && R && R.show && <div style={{ fontSize: '.86rem', color: 'var(--ink2)', marginTop: 4 }}>עם ההוצאות הרגילות: בערך <Xn k="low"><b>{fMoney(Math.round(R.low / 100) * 100)}</b></Xn></div>}
      <div className="sub" style={{ marginTop: 12, paddingTop: 11, borderTop: '1px solid color-mix(in srgb, currentColor 12%, transparent)', maxWidth: 760 }}>
        {UI.flowStress && 'כל ההוצאות הועלו ב-15% מול המתוכנן. '}{note}{deeper}
        {per === 1 && <div className="note mt6">נכון לרגע זה: רק מה שכבר ידוע, בלי סופר, דלק וכו'. בשבילם יש "מותר להוציא היום".</div>}
        {per >= 2 && <div className="note mt6">התקופה הנוכחית: רק מה שכבר ידוע. מהתקופה הבאה: {strictMode() ? 'כולל הערכה להוצאות רגילות (חיוב כרטיס טיפוסי והוצאות שוטפות מהעו"ש)' : 'חיוב כרטיס טיפוסי, בלי הוצאות שוטפות מהעו"ש'}.{per >= 3 ? ' בטווח ארוך התחזית פחות מדויקת.' : ''}</div>}
      </div>
    </div>
  );
}

/** סימולציה: מה אם חיוב היה יורד ביום אחר */
function SimBox({ days, lowBal }) {
  const ids = Object.keys(UI.flowSim);
  if (!ids.length) return null;
  const base = lowPoint(project(days, { stress: UI.flowStress }));
  const gain = Math.round(lowBal - base.balance);
  return (
    <div className="mb12" style={{ padding: '10px 12px', borderRadius: 12, background: 'var(--gold-l)', fontSize: '.82rem', lineHeight: 1.6 }}>
      <b>🧪 סימולציה</b> · {ids.map((id) => { const r = S.rec.find((x) => String(x.id) === String(id)); return r ? r.description + ': ' + r.day_of_month + ' ← ' + UI.flowSim[id] : ''; }).join(' · ')}<br />
      השפל {gain >= 0 ? <>משתפר ב-<b className="green">{fMoney(gain)}</b></> : <>מחמיר ב-<b className="red">{fMoney(-gain)}</b></>} (בלי השינוי: {fMoney(base.balance)})
      <div className="row mt6" style={{ gap: 6 }}>
        <button className="ubtn ubtn-outline ubtn-sm-pad" onClick={() => setUi({ flowSim: {} })}>בטל סימולציה</button>
        <button className="ubtn ubtn-outline ubtn-sm-pad" onClick={async () => {
          if (!(await confirmDialog({ title: 'שינוי ימי חיוב', body: 'לשנות את יום החיוב בחיובים הקבועים לפי הסימולציה? זה משנה רק את התחזית באפליקציה, לא את החיוב בפועל.' }))) return;
          await applySim(UI.flowSim); setUi({ flowSim: {} });
        }}>הפוך לקבוע</button>
      </div>
    </div>
  );
}
async function simMove(id) {
  const r = S.rec.find((x) => String(x.id) === String(id));
  if (!r) return;
  const cur = UI.flowSim[r.id] != null ? UI.flowSim[r.id] : r.day_of_month;
  const v = await promptDialog({ title: 'מה אם יורד ביום אחר?', body: '"' + r.description + '" יורד היום ב-' + r.day_of_month + ' לחודש. יום חדש (1-31):', type: 'number', initial: cur, okLabel: 'הצג' });
  if (v == null) return;
  const n = parseInt(v, 10);
  if (!(n >= 1 && n <= 31)) return toast('יום לא תקין');
  const sim = { ...UI.flowSim };
  if (n === Number(r.day_of_month)) delete sim[r.id]; else sim[r.id] = n;
  setUi({ flowSim: sim });
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function FlowStats({ per, days }) {
  const p = project(days, ctxOf()), end = p[p.length - 1].balance, ch = end - balEst().amount;
  const stat = (v, k, wk) => (
    <div className="stat"><div className="stat-v" style={{ color: v < 0 ? 'var(--red)' : 'var(--green)' }}><Xn k={wk}>{fShort(v)}</Xn></div><div className="stat-k">{k}</div></div>
  );
  return (
    <div className="grid2">
      {stat(end, (per === 1 ? 'בסוף התקופה' : per === 2 ? 'בסוף התקופה הבאה' : 'בעוד ' + per + ' תקופות') + ' (' + wDY(rangeEnd(per)) + ')', 'flow_end')}
      {stat(ch, 'שינוי מהיום', 'flow_end')}
    </div>
  );
}

/* ── מה מרכיב את התחזית: יתרה, הכנסות, חיובים מהעו"ש, חיוב הכרטיס ── */
function Part({ icon, title, amount, sign, sub, alert, why, whyArg, children }) {
  const [open, setOpen] = useState(false);
  const col = sign === '+' ? 'var(--green)' : sign === '-' ? 'var(--red)' : amount < 0 ? 'var(--red)' : 'var(--ink)';
  const shown = sign ? sign + abs$(amount) : fMoney(amount);
  return (
    <div style={{ borderTop: '1px solid var(--border)', padding: '11px 0' }}>
      <div className="row clickable" style={{ gap: 10 }} onClick={() => setOpen(!open)}>
        <div className="part-ico">{icon}</div>
        <div className="grow"><div style={{ fontSize: '.93rem', fontWeight: 700 }}>{title}</div><div className="note">{sub}</div></div>
        <div className="nowrap" style={{ fontSize: '.95rem', fontWeight: 800, color: col }}>{why ? <Xn k={why} arg={whyArg}>{shown}</Xn> : shown}</div>
        <span className="muted" style={{ fontSize: '.86rem', width: 12, textAlign: 'center' }}>{open ? '▴' : '▾'}</span>
      </div>
      {alert && <div className="mt6">{alert}</div>}
      {open && <div className="box2 mt6">{children}</div>}
    </div>
  );
}
function Line({ name, meta, amount, onClick, muted }) {
  return (
    <div className={'row-b' + (onClick ? ' clickable' : '')} onClick={onClick} style={{ fontSize: '.82rem', padding: '4px 0', color: muted ? 'var(--ink3)' : 'var(--ink2)' }}>
      <span style={{ minWidth: 0 }}><span className="ellipsis" style={{ display: 'block' }}>{name}</span>{meta && <span style={{ display: 'block', color: 'var(--ink3)', fontSize: '.8rem', lineHeight: 1.4 }}>{meta}</span>}</span>
      <span className="nowrap" style={{ fontWeight: 600 }}>{fMoney(amount)}{onClick && <span style={{ color: 'var(--ink4)' }}> ✎</span>}</span>
    </div>
  );
}
function occMeta(items) {
  if (!items || !items.length) return '';
  const late = items.filter((it) => it.late).length;
  if (items.length === 1) return late ? '⏳ טרם נקלט, נספר מחר' : 'צפוי ' + wD(items[0].d);
  const same = items.every((it) => Math.round(it.amount) === Math.round(items[0].amount));
  return (same ? fMoney(items[0].amount) + ' × ' + items.length : items.length + ' פעמים') + ' · ' + wDY(items[0].d) + ' עד ' + wDY(items[items.length - 1].d) + (late ? ' · כולל ' + (late === 1 ? 'אחד' : late) + ' שטרם נקלט' : '');
}

function Parts({ per, days }) {
  const X = projParts(days, { sim: UI.flowSim }), RS = X.rs;
  const rangeTxt = rangePhrase(per) + ' (עד ' + wDY(rangeEnd(per)) + ')';
  const inRange = (list) => Math.round(list.reduce((s, r) => s + ((RS[r.id] || {}).sum || 0), 0));
  const paidN = (list) => list.filter((r) => { const o = recNow(r); return o && o.st === 'paid'; }).length;
  const recRows = (list) => list.slice().sort((a, b) => (Number(a.day_of_month) || 1) - (Number(b.day_of_month) || 1)).map((r) => {
    const x = RS[r.id] || { sum: 0 }, o = recNow(r), base = 'ב-' + r.day_of_month + (recFreqLabel(r) ? ' · ' + recFreqLabel(r) : '');
    const lateTxt = x.late ? ' · מועד שעבר ועוד לא נסרק: כבר בתוך "יתרה היום"' : '';
    const meta = (x.sum ? base + ' · ' + occMeta(x.items) + (o && o.st === 'paid' ? ' · הקודם ירד ' + wD(o.t.date) : '')
      : base + ' · ' + (o && o.st === 'paid' ? recStatusTxt(o) + ' · כבר ביתרה, לא נספר שוב' : x.late ? '' : 'המועד הבא אחרי סוף הטווח')) + lateTxt;
    return <Line key={r.id} name={r.description} meta={meta} amount={x.sum} onClick={() => openWhy('rec', r.id)} muted={!x.sum} />;
  });
  const addBtn = <button className="ubtn ubtn-outline w100 mt6" style={{ padding: 6, fontSize: '.82rem' }} onClick={() => openRecModal()}>➕ הוסף חיוב קבוע</button>;
  const incs = S.rec.filter((r) => r.type === 'income');
  // חיוב קבוע בכרטיס נספר כאן רק כשאין עדיין חיוב כרטיס בתחזית. אחרת הוא בתוך חיוב הכרטיס
  const cashRec = S.rec.filter((r) => r.type !== 'income' && ((r.source || 'cash') !== 'credit' || (RS[r.id] || {}).sum));
  return (
    <>
      {S.set.balDate && <BalancePart />}
      {per > 1 && <div className="box2 note mb8" style={{ color: 'var(--ink2)' }}>הסכומים כאן הם {rangeTxt}. חיוב חודשי נספר בכל תקופה שבו הוא יורד. לסכום של תקופה אחת, בחר "התקופה הנוכחית" למטה.</div>}
      <Part icon="💰" title="הכנסות קבועות" amount={inRange(incs)} sign="+" sub={rangeTxt + (paidN(incs) ? ' · ' + paidN(incs) + ' כבר נכנסו' : '')} why="flow_inc">
        {incs.length ? recRows(incs) : <div className="note">אין הכנסות קבועות. בלעדיהן התחזית רואה רק הוצאות.</div>}{addBtn}
      </Part>
      <Part icon="🏦" title='חיובים קבועים מהעו"ש' amount={inRange(cashRec)} sign="-" sub={rangeTxt + (paidN(cashRec) ? ' · ' + paidN(cashRec) + ' כבר ירדו' : '')} why="flow_cash">
        {cashRec.length ? recRows(cashRec) : <div className="note">אין חיובים קבועים מהעו"ש.</div>}
        <div className="note mt6">חיוב שכבר ירד נמצא בתוך היתרה של היום, ולכן הוא 0 כאן. גם חיוב שהתאריך שלו עבר ועוד לא נקלט בסריקה כבר בתוך "יתרה היום". לחיצה על חיוב מראה את המצב שלו ומאפשרת עריכה.</div>
        {addBtn}
      </Part>
      <CardPart days={days} per={per} rangeTxt={rangeTxt} cEvs={X.cards} />
      {X.nReal > 0 && <Part icon="📌" title='תנועות עו"ש שכבר רשומות קדימה' amount={-X.real} sub={X.nReal + ' תנועות עם תאריך עתידי, מהסריקה או שהוספת'}>
        <div className="note">פעולות שנשמרו לתאריך שעוד לא הגיע. הן ייספרו ביתרה ביום שלהן.</div>
      </Part>}
      {X.commit > 0 && <Part icon="🧾" title="תשלומים ידועים" amount={X.commit} sign="-" sub="אין עדיין חיוב כרטיס בתחזית, ולכן הם בנפרד"><div className="note">תשלומים שנמצאו בדוחות קודמים.</div></Part>}
      {X.variable > 0 && <Part icon="🛒" title='הוצאות שוטפות מהעו"ש (הערכה)' amount={X.variable} sign="-" sub={'בתקופות הבאות בלבד · ' + X.nVar + ' שורות שבועיות'}>
        <div className="note">ביט, מזומן והוראות חד-פעמיות: חציון 3 התקופות הקודמות, מפוזר לאורך התקופה. בתקופה הנוכחית הן לא כאן, כי בשבילן יש "מותר להוציא היום".</div>
      </Part>}
      <div className="row-b" style={{ borderTop: '2px solid var(--border)', padding: '11px 0 0', fontWeight: 800 }}>
        <span>= {per === 1 ? 'בסוף התקופה' : 'בסוף הטווח'} ({wDY(rangeEnd(per))})</span>
        <span style={{ color: X.end < 0 ? 'var(--red)' : 'var(--green)' }}>{fMoney(X.end)}</span>
      </div>
      {UI.flowStress && <div className="note mt6">בלי התרחיש הפסימי. עם התרחיש, המספרים למעלה נמוכים יותר.</div>}
    </>
  );
}

function BalancePart() {
  const [v, setV] = useState('');
  const bt = balToday(), E = balEst(), Q = balTodayParts(), today = todayISO();
  const stale = S.set.balDate < today;
  let sub = stale ? 'יתרה של ' + wD(S.set.balDate) + (bt.count ? ' + ' + bt.count + ' תנועות עו"ש מאז' : '') : 'עודכנה היום';
  if (Q) sub = 'פתיחת התקופה (' + wD(Q.ps) + ') + ' + (Q.ni + Q.ne + Q.cards.length) + ' תנועות עו"ש בתקופה';
  const bank = scanCoverage().find((c) => c.src === 'cash');
  const ago = bank ? Math.round((new Date(today + 'T12:00:00') - new Date(bank.date + 'T12:00:00')) / 86400000) : 0;
  function save() {
    const n = parseFloat(String(v).replace(/[^0-9.-]/g, ''));
    if (isNaN(n)) return toast('הזן יתרה');
    saveBalance(n, today, true);
    setV('');
    toast('✅ יתרה להיום: ' + fMoney(n));
  }
  return (
    <Part icon="🏦" title="יתרה היום" amount={E.amount} sub={sub} why="bal"
      alert={ago >= 10 && <div style={{ fontSize: '.8rem', color: 'var(--red)' }}>⚠️ העו"ש לא נסרק {ago} ימים. היתרה לא כוללת תנועות מאז.</div>}>
      {Q ? <>
        <Line name="פתיחת התקופה = סגירת התקופה הקודמת" meta={wD(Q.ps)} amount={Q.open} />
        {Q.inc > 0 && <Line name='הכנסות בעו"ש בתקופה' meta={Q.ni + ' תנועות'} amount={Q.inc} />}
        {Q.exp > 0 && <Line name='הוצאות בעו"ש בתקופה' meta={Q.ne + ' תנועות'} amount={-Q.exp} />}
        {Q.cards.map((c, i) => <Line key={i} name="חיוב כרטיס אשראי" meta={wD(c.iso)} amount={-c.amount} />)}
        {Q.late.map((x, i) => <Line key={'l' + i} name={x.name} meta={wD(x.iso) + ' · קרה, עוד לא נסרק'} amount={x.amount} />)}
      </> : <>
        {stale && <Line name="יתרת פתיחה מההגדרות" meta={(S.set.balEod ? 'סוף יום ' : 'תחילת יום ') + wD(S.set.balDate)} amount={bt.base} />}
        {stale && bt.count > 0 && <Line name="תנועות עו&quot;ש שנסרקו מאז" meta={bt.count + ' תנועות'} amount={bt.delta} />}
        {E.late.map((x, i) => <Line key={i} name={x.name} meta={wD(x.iso) + ' · קרה, עוד לא נסרק'} amount={x.amount} />)}
      </>}
      <div className="note mt6">התזרים מתחיל מהיתרה הזו ומוסיף רק מה שיקרה מהיום והלאה. אם יש בעו"ש תנועות שעוד לא נסרקו, הזן כאן את היתרה של היום. יתרת פתיחה לתאריך אחר: <button className="link" onClick={() => navigate('Set')}>בהגדרות</button>.</div>
      <div className="row mt10" style={{ gap: 6 }}>
        <input type="number" inputMode="decimal" className="inline-input" placeholder='יתרה בעו"ש היום ₪' value={v} onChange={(e) => setV(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
        <button className="ubtn ubtn-outline btn-xs" onClick={save}>עדכן</button>
      </div>
    </Part>
  );
}

function CardPart({ per, rangeTxt, cEvs }) {
  const [manual, setManual] = useState('');
  const c = cardChargeAmount(), fut = cardFutureEstimate(), pace = cardPace();
  const nk = Object.keys(cardChargeEvents(new Date(), 62))[0], nd = nk ? new Date(nk + 'T12:00:00') : null;
  const mk = cardManualFor(cardCurKey());
  const credRec = S.rec.filter((r) => r.type !== 'income' && (r.source || 'cash') === 'credit');
  const commitFut = S.commits.filter((x) => String(x.due_date) >= todayISO());
  const hi = pace && pace.pct >= 15, lo = pace && pace.pct <= -15;
  const paceBox = pace && (
    <div style={{ padding: '7px 10px', borderRadius: 9, fontSize: '.8rem', lineHeight: 1.55, background: hi ? 'var(--red-l)' : 'var(--surface2)' }}>
      <b style={{ color: hi ? 'var(--red)' : lo ? 'var(--green)' : 'var(--ink2)' }}>{hi ? '⚠️ קצב גבוה' : lo ? '✓ קצב נמוך' : 'קצב רגיל'}</b>
      {' '}· יום {pace.day}/{pace.len} · <Xn k="flow_cpace">{fMoney(pace.soFar)} מול {fMoney(pace.usual)}</Xn> בדרך כלל{pace.usual ? ' (' + (pace.pct > 0 ? '+' : '') + pace.pct + '%)' : ''}
      <br /><span className="muted">בקצב הזה החיוב יגיע לכ-{fMoney(pace.proj)}</span>
    </div>
  );
  const estWhy = fut.amount ? 'הערכה: חציון ' + fut.n + ' תקופות סגורות' : 'הערכה: כמו החיוב הקרוב, כי עוד לא נסרקו חודשים קודמים';
  const multi = cEvs.length > 1, first = cEvs[0], later = cEvs.slice(1);
  const total = Math.round(cEvs.reduce((s, x) => s + x.a, 0));
  const sub = multi ? cEvs.length + ' חיובים ' + rangeTxt + (nd ? ' · הקרוב ב-' + wD(nd) : '')
    : c ? (c.src === 'manual' ? 'סכום ידני' : 'אשראי בתקופה ' + periodShort(cardCurKey())) + (nd ? ' · יורד ב-' + wD(nd) : '') : 'אין עדיין פעולות אשראי בתקופה';
  function saveManual() {
    const n = parseFloat(String(manual).replace(/[^0-9.]/g, ''));
    if (!(n > 0)) return toast('הזן סכום');
    setCardManual(n); setManual('');
    toast('✅ חיוב הכרטיס: ' + fMoney(n));
  }
  return (
    <Part icon="💳" title={multi ? 'חיובי כרטיס אשראי' : 'חיוב כרטיס אשראי'} amount={total} sign="-" sub={sub}
      why={multi ? 'flow_cards' : 'flow_card'} alert={hi && paceBox}>
      {!hi && paceBox && <div className="mb8">{paceBox}</div>}
      {multi && <>
        {first && <Line name="החיוב הקרוב" meta={'צפוי ' + wD(first.d) + ' · ' + (first.est ? estWhy : 'העסקאות שכבר נרשמו בתקופה')} amount={first.a} onClick={() => openWhy('flow_card', getIso(first.d))} />}
        {later.length > 0 && <Line name="חיובים בחודשים הבאים" meta={occMeta(later.map((x) => ({ d: x.d, amount: x.a }))) + ' · ' + estWhy} amount={later.reduce((s, x) => s + x.a, 0)} onClick={() => openWhy('flow_card', getIso(later[0].d))} />}
        <div className="divider" /><div style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--ink2)' }}>פירוט החיוב הקרוב</div>
      </>}
      {c && c.src === 'manual' && <Line name="סכום ידני" meta="כולל הכל" amount={c.amount} />}
      {c && c.src !== 'manual' && <>
        <Line name="נרשם באשראי עד היום" amount={c.base} />
        {(c.parts || []).map((x, i) => <Line key={i} name={'+ ' + x.name} meta={wD(x.date)} amount={x.amount} />)}
        {(c.parts || []).length > 0 && <div className="note mt6">חיובים קבועים בכרטיס ותשלומים שעוד לא נמצאו בסך האשראי: כאלה שעוד יגיעו עד סוף התקופה, וכאלה שהתאריך שלהם עבר אבל הם עוד לא נקלטו בסריקה.</div>}
      </>}
      {fut.amount > 0 && per === 1 && <><div className="divider" /><Line name="חיובים בחודשים הבאים" meta={'חציון ' + fut.n + ' תקופות'} amount={fut.amount} /></>}
      {credRec.length > 0 && <>
        <div className="divider" /><div style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--ink2)' }}>חיובים קבועים בכרטיס, בחיוב של {nd ? wD(nd) : 'החודש'}</div>
        {credRec.slice().sort((a, b) => (Number(a.day_of_month) || 1) - (Number(b.day_of_month) || 1)).map((r) => {
          const cur = recCardCur(r);
          return <Line key={r.id} name={r.description} meta={'ב-' + r.day_of_month + (recFreqLabel(r) ? ' · ' + recFreqLabel(r) : '') + ' · ' + recStatusTxt(recNow(r)) + (cur && cur.st === 'paid' ? ' · כבר בסך האשראי' : '')}
            amount={cur ? cur.amount : 0} onClick={() => openWhy('rec', r.id)} muted={!cur || cur.st === 'paid'} />;
        })}
        <div className="note mt6">כלולים בחיוב הכרטיס ולא מופיעים בתזרים בנפרד. חיוב שכבר נקלט נמצא בתוך "נרשם באשראי", ולכן לא נוסף שוב (באפור).</div>
      </>}
      {commitFut.length > 0 && <>
        <div className="divider" /><div style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--ink2)' }}>תשלומים ידועים</div>
        {commitFut.map((x) => (
          <div className="row" key={x.id} style={{ gap: 6 }}>
            <button className="sheet-x" style={{ fontSize: '.9rem' }} onClick={() => deleteCommit(x.id)} aria-label="מחק">×</button>
            <div className="grow"><Line name={x.description} meta={wD(x.due_date)} amount={Math.abs(Number(x.amount) || 0)} muted /></div>
          </div>
        ))}
        <div className="note mt6">תשלום שמועדו בחיוב הקרוב נוסף לחיוב הכרטיס, כי הוא עוד לא בעסקאות שנרשמו. תשלומים בחודשים שאחריו כבר בתוך ההערכה לפי תקופות קודמות.</div>
      </>}
      <div className="row mt10" style={{ gap: 6 }}>
        <input type="number" inputMode="decimal" className="inline-input" placeholder={mk ? String(mk) : 'סכום ידני לחיוב הקרוב ₪'} value={manual} onChange={(e) => setManual(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
        <button className="ubtn ubtn-outline btn-xs" onClick={saveManual}>שמור</button>
        {mk > 0 && <button className="ubtn ubtn-ghost btn-xs" onClick={() => { setCardManual(0); toast('חזרה לסך פעולות האשראי'); }}>בטל</button>}
      </div>
    </Part>
  );
}
const getIso = fISO;

/* ── ציר הזמן, מקובץ לפי תקופה ── */
function Timeline({ days }) {
  const [openK, setOpenK] = useState({});
  const p = project(days, ctxOf()), low = lowPoint(p);
  const withEv = p.filter((d) => d.events.length);
  if (!withEv.length) return <div className="empty-s">אין נתונים בתקופה.</div>;
  const groups = [], gm = {};
  withEv.forEach((d) => {
    const k = getKey(d.date);
    if (!gm[k]) { gm[k] = { key: k, days: [], inc: 0, exp: 0, low: d }; groups.push(gm[k]); }
    const g = gm[k];
    g.days.push(d);
    if (d.balance < g.low.balance) g.low = d;
    d.events.forEach((e) => { if (e.type === 'income') g.inc += e.amount; else g.exp += e.amount; });
  });
  return (
    <>
      <div className="note mb8" style={{ paddingBottom: 9, borderBottom: '1px solid var(--border)' }}>רק מה שעוד יקרה, מהיום והלאה. עסקאות בכרטיס לא מופיעות בנפרד, הן בתוך חיוב הכרטיס. לחיצה על חיוב קבוע: סימולציה של יום אחר.</div>
      {groups.map((g, gi) => {
        const open = groups.length === 1 || (openK[g.key] !== undefined ? openK[g.key] : gi === 0);
        const last = g.days[g.days.length - 1], hasLow = g.days.some((d) => d.key === low.key);
        return (
          <div key={g.key} style={{ marginTop: gi ? 10 : 0 }}>
            {groups.length > 1 && (
              <div className={'tl-grp' + (hasLow ? ' low' : '')} onClick={() => setOpenK({ ...openK, [g.key]: !open })}>
                <div><div style={{ fontSize: '.93rem', fontWeight: 800 }}>{periodShort(g.key)}{hasLow && <span className="red" style={{ fontSize: '.82rem' }}> · השפל</span>}</div>
                  <div className="note" style={{ color: 'var(--gold)' }}>{g.key > curKey() ? (strictMode() ? 'הערכה: כולל הוצאות שוטפות' : 'הערכה: חיוב כרטיס טיפוסי') : 'רק מה שידוע'}</div>
                  <div className="note">{fShort(g.inc, '+')} · {fShort(g.exp, '−')} · שפל {fShort(g.low.balance)}</div></div>
                <div className="nowrap" style={{ textAlign: 'left' }}><div className="note">בסוף התקופה</div>
                  <div style={{ fontSize: '.93rem', fontWeight: 800, color: last.balance < 0 ? 'var(--red)' : 'var(--green)' }}><Xn k="flow_grp" arg={g.key}>{fMoney(last.balance)}</Xn></div></div>
                <span className="muted">{open ? '▴' : '▾'}</span>
              </div>
            )}
            {open && g.days.map((d) => <Day key={d.key} d={d} isLow={d.key === low.key} />)}
          </div>
        );
      })}
    </>
  );
}
function Day({ d, isLow }) {
  return (
    <div className={'tl-day' + (isLow ? ' low' : '')}>
      <div className="tl-date"><b>{d.date.getDate()}</b><span>{MONTHS[d.date.getMonth()]}</span></div>
      <div className="grow">
        {d.events.map((e, i) => {
          const click = e.lateCard ? () => openWhy('flow_card', e.iso) : e.late && e.recId != null ? () => openWhy('rec', e.recId) : e.recId != null && e.type !== 'income' ? () => simMove(e.recId) : null;
          return (
            <div className="tl-ev" key={i}>
              <span className={'ellipsis' + (click ? ' clickable dotted' : '')} style={e.late ? { color: 'var(--gold)' } : undefined} onClick={click || undefined}>
                {e.name}{e.moved && <span style={{ color: 'var(--accent)', fontSize: '.82rem' }}> הוזז</span>}
                {e.late && <span className="muted" style={{ fontSize: '.8rem' }}> · כבר בתוך "יתרה היום"</span>}
              </span>
              <span className="nowrap" style={{ fontWeight: 700, color: e.type === 'income' ? 'var(--green)' : 'var(--red)' }}>
                {e.card ? <Xn k="flow_card" arg={e.iso || d.key}>{abs$(e.amount, '−')}</Xn> : abs$(e.amount, e.type === 'income' ? '+' : '−')}
              </span>
            </div>
          );
        })}
        <div style={{ fontSize: '.8rem', color: 'var(--ink3)', marginTop: 3 }}>יתרה אחרי: <b style={{ color: d.balance < 0 ? 'var(--red)' : 'var(--green)' }}><Xn k="flow_day" arg={d.key}>{fMoney(d.balance)}</Xn></b>{isLow ? ' · נקודת השפל' : ''}</div>
      </div>
    </div>
  );
}

/* ── תבניות חוזרות כפולות ── */
function DupRec() {
  const dups = findDupRec();
  if (!dups.length) return null;
  async function del(r) {
    if (!(await confirmDialog({ title: 'מחיקת תבנית', body: 'למחוק את התבנית "' + r.description + '"? העסקאות עצמן לא ייפגעו.', okLabel: 'מחק', danger: true }))) return;
    try { await deleteRec(r.id); toast('🗑️ התבנית נמחקה'); } catch (e) { toast('שגיאה: ' + e.message); }
  }
  return (
    <div className="alert-gold">
      <div className="alert-title">⚠️ תבניות חוזרות כפולות</div>
      <div className="note mb8" style={{ color: 'var(--ink2)' }}>אותו חיוב מוגדר יותר מפעם אחת. בתחזית הוא נספר פעם אחת בלבד, אבל כדאי למחוק את המיותרות.</div>
      {dups.map((g, gi) => (
        <div key={gi} style={{ marginBottom: 9 }}>
          {g.map((r, i) => (
            <div key={r.id} className="row" style={{ padding: '6px 0', borderBottom: i < g.length - 1 ? '1px solid var(--border)' : undefined }}>
              <span className="grow ellipsis" style={{ fontSize: '.86rem' }}>{r.description}</span>
              <span className="note nowrap">ב-{r.day_of_month} לחודש</span>
              <b className="nowrap" style={{ fontSize: '.86rem' }}>{fMoney(r.amount)}</b>
              {i > 0 ? <button className="ubtn ubtn-ghost btn-xs danger-text" onClick={() => del(r)}>×</button> : <span className="green nowrap" style={{ fontSize: '.82rem' }}>נשמר</span>}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ── חיובים שחוזרים ולא מוגדרים ── */
function UndeclaredSubs() {
  const [open, setOpen] = useState(false);
  const subs = findUndeclaredSubs();
  if (!subs.length) return null;
  async function add(s) {
    try { await recWrite(null, { description: s.name, amount: s.amount, type: 'expense', cat: s.cat || 'other', day_of_month: s.day, source: s.source || 'cash' }); toast('✅ נוסף לחיובים קבועים'); }
    catch (e) { toast('⚠️ ' + e.message); }
  }
  return (
    <div className="panel">
      <div className="sec row-b clickable" style={{ marginBottom: open ? 10 : 0 }} onClick={() => setOpen(!open)}>
        <span>🔁 {subs.length} חיובים שחוזרים ולא מוגדרים</span><span className="muted">{open ? '▴' : '▾'}</span>
      </div>
      {open && <>
        <div className="sub mb8" style={{ fontSize: '.86rem' }}>חיובים שחוזרים בקביעות אבל לא מוגדרים כחיוב קבוע, ולכן חסרים בתחזית:</div>
        {subs.map((s, i) => (
          <div key={i} className="row" style={{ gap: 9, padding: '9px 0', borderBottom: i < subs.length - 1 ? '1px solid var(--border)' : undefined }}>
            <div className="grow"><div className="ellipsis" style={{ fontSize: '.93rem', fontWeight: 700 }}>{s.name}</div>
              <div className="note">{s.months} חודשים · בערך ב-{s.day} לחודש · {fMoney(s.yearly)} בשנה</div></div>
            <b className="nowrap red" style={{ fontSize: '.93rem' }}>{fMoney(s.amount)}</b>
            <button className="ubtn ubtn-outline ubtn-sm-pad" onClick={() => add(s)}>הוסף</button>
          </div>
        ))}
      </>}
    </div>
  );
}

function FlowSettings() {
  const [open, setOpen] = useState(false);
  const [lim, setLim] = useState(S.set.odLimit || '');
  const [buf, setBuf] = useState(S.set.bufTarget > 0 ? S.set.bufTarget : '');
  function save() {
    saveSetting('od_limit', parseFloat(lim) || 0);
    const bv = parseFloat(buf);
    saveSetting('buf_target', bv > 0 ? bv : S.set.bufTarget < 0 ? -1 : 0);
    toast('✅ נשמר');
  }
  return (
    <div className="panel">
      <div className="sec row-b clickable" style={{ marginBottom: open ? 10 : 0 }} onClick={() => setOpen(!open)}><span>⚙️ מסגרת, כרית ותרחיש פסימי</span><span className="muted">{open ? '▴' : '▾'}</span></div>
      {open && <>
        <div className="field"><label>מסגרת אשראי ₪</label><input type="number" inputMode="decimal" placeholder="15000" value={lim} onChange={(e) => setLim(e.target.value)} /></div>
        <div className="field"><label>יעד כרית ביטחון ₪</label><input type="number" inputMode="decimal" placeholder="14000" value={buf} onChange={(e) => setBuf(e.target.value)} /></div>
        <button className="btn btn-full btn-dark" onClick={save}>שמירה</button>
        <button className={'ubtn w100 mt12 ' + (UI.flowStress ? 'ubtn-danger' : 'ubtn-outline')} onClick={() => setUi({ flowStress: !UI.flowStress })} aria-pressed={UI.flowStress}>
          {UI.flowStress ? '✓ תרחיש פסימי פעיל' : 'תרחיש פסימי: כל ההוצאות +15%'}
        </button>
        <div className="note mt12">התזרים מתחיל מהיתרה של היום, ומוסיף רק תנועות מהיום והלאה. חיובי אשראי בודדים לא נספרים בנפרד, כדי לא לספור פעמיים את חיוב הכרטיס החודשי.</div>
      </>}
    </div>
  );
}

